import { Link, useNavigate } from 'react-router-dom';
import { PlusIcon, CalendarIcon, ServerIcon } from '@heroicons/react/24/outline';
import { useState } from 'react';
import PageHeader from '../components/layout/PageHeader';
import { useMcpConnections, useDeleteMcpConnection } from '../hooks/useMcpConnections';
import { useListState } from '../hooks/useListState';
import { useSortableData } from '../hooks/useSortableData';
import { SearchBar } from '../components/ui/SearchBar';
import { Pagination } from '../components/ui/Pagination';
import { ViewToggle } from '../components/ui/ViewToggle';
import { DataTable, DataTableHead, DataTableBody, DataTableRow, DataTableCell, TableHeader } from '../components/ui/DataTable';
import { SortableTableHeader } from '../components/ui/SortableTableHeader';
import McpConnectionCard from '../components/mcpConnections/McpConnectionCard';
import { useFlag } from '../hooks/useFeatureFlags';
import DeleteResourceControl from '../components/shared/DeleteResourceControl';

export default function McpConnectionsPage() {
  const navigate = useNavigate();
  // The create affordance is gated by its manifest flag, whose
  // member-and-above cohorts carry the role rule (task 692).
  const canCreate = useFlag('innovate.fe.mcp-connections.create');
  const { page, limit, search, viewMode, setPage, setLimit, setSearch, setViewMode } = useListState({
    entityType: 'mcp-connections',
  });

  const [statusFilter, setStatusFilter] = useState<string>('');

  const { data, isLoading, error } = useMcpConnections(page, limit, {
    search: search || undefined,
    isActive: statusFilter === '' ? undefined : statusFilter === 'active',
  });
  const connections = data?.data || [];
  const pagination = data?.pagination;

  const { sortedData, sortConfig, handleSort } = useSortableData(connections, 'updatedAt', 'desc');

  const deleteMcpConnection = useDeleteMcpConnection();

  const getAuthTypeBadgeClasses = (authType: string) => {
    switch (authType) {
      case 'bearer':
        return 'bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200';
      case 'api-key':
        return 'bg-purple-100 dark:bg-purple-900 text-purple-800 dark:text-purple-200';
      case 'basic':
        return 'bg-orange-100 dark:bg-orange-900 text-orange-800 dark:text-orange-200';
      default:
        return 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200';
    }
  };

  const getAuthTypeLabel = (authType: string) => {
    switch (authType) {
      case 'bearer':
        return 'Bearer';
      case 'api-key':
        return 'API Key';
      case 'basic':
        return 'Basic';
      default:
        return 'None';
    }
  };

  const actions = canCreate ? (
    <Link
      to="/mcp-connections/new"
      className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 dark:bg-indigo-700 hover:bg-indigo-700 dark:hover:bg-indigo-800"
    >
      <PlusIcon className="-ml-1 mr-2 h-4 w-4" />
      Add MCP Connection
    </Link>
  ) : undefined;

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-red-500 dark:text-red-400">Error loading MCP connections. Please try again.</div>
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title="MCP Connections"
        description={pagination ? `${pagination.total} ${pagination.total === 1 ? 'connection' : 'connections'}${search ? ' found' : ' total'}` : 'Model Context Protocol server connections'}
        actions={actions}
      />

      <div className="mt-6">
        {/* Search and Filter Section */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4 mb-6">
          <div className="flex items-center gap-4">
            <div className="flex-1">
              <SearchBar
                value={search}
                onChange={setSearch}
                placeholder="Search connections..."
              />
            </div>
            <select
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setPage(1);
              }}
              className="block w-40 rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-sm"
            >
              <option value="">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
            <ViewToggle viewMode={viewMode} onViewModeChange={setViewMode} />
          </div>
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center h-64">
            <div className="text-gray-500 dark:text-gray-400">Loading MCP connections...</div>
          </div>
        ) : sortedData.length === 0 ? (
          <div className="text-center py-12">
            <ServerIcon className="mx-auto h-12 w-12 text-gray-400" />
            <div className="text-gray-500 dark:text-gray-400 mb-4 mt-4">
              {search || statusFilter
                ? `No MCP connections found${search ? ` matching "${search}"` : ''}${statusFilter ? ` with status "${statusFilter}"` : ''}`
                : 'No MCP connections found. Create your first connection to get started.'}
            </div>
            {!search && !statusFilter && canCreate && (
              <Link
                to="/mcp-connections/new"
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700"
              >
                <PlusIcon className="-ml-1 mr-2 h-4 w-4" />
                Create your first connection
              </Link>
            )}
          </div>
        ) : viewMode === 'grid' ? (
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {sortedData.map((connection) => (
              <McpConnectionCard key={connection.id} connection={connection} />
            ))}
          </div>
        ) : (
          <DataTable>
            <DataTableHead>
              <SortableTableHeader
                label="Connection"
                field="name"
                sortField={sortConfig.field}
                sortDirection={sortConfig.direction}
                onSort={handleSort}
                className="w-1/4 min-w-0"
              />
              <SortableTableHeader
                label="URL"
                field="url"
                sortField={sortConfig.field}
                sortDirection={sortConfig.direction}
                onSort={handleSort}
                className="w-1/4"
              />
              <TableHeader className="w-24">Auth Type</TableHeader>
              <TableHeader className="w-20">Status</TableHeader>
              <SortableTableHeader
                label="Updated"
                field="updatedAt"
                sortField={sortConfig.field}
                sortDirection={sortConfig.direction}
                onSort={handleSort}
                className="w-32"
              />
              <TableHeader className="w-20">
                <span className="sr-only">Actions</span>
              </TableHeader>
            </DataTableHead>
            <DataTableBody>
              {sortedData.map((connection) => (
                <DataTableRow
                  key={connection.id}
                  onClick={() => navigate(`/mcp-connections/${connection.id}`)}
                >
                  <DataTableCell>
                    <div className="min-w-0">
                      <div className="text-sm font-medium text-gray-900 dark:text-white truncate">
                        {connection.name}
                      </div>
                      {connection.description && (
                        <div className="text-xs text-gray-500 dark:text-gray-400 truncate">
                          {connection.description}
                        </div>
                      )}
                    </div>
                  </DataTableCell>
                  <DataTableCell>
                    <span className="text-sm font-mono text-gray-600 dark:text-gray-300 truncate">
                      {connection.url}
                    </span>
                  </DataTableCell>
                  <DataTableCell>
                    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getAuthTypeBadgeClasses(connection.authType)}`}>
                      {getAuthTypeLabel(connection.authType)}
                    </span>
                  </DataTableCell>
                  <DataTableCell>
                    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                      connection.isActive
                        ? 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200'
                        : 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200'
                    }`}>
                      {connection.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </DataTableCell>
                  <DataTableCell className="whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                    <div className="flex items-center">
                      <CalendarIcon className="h-4 w-4 mr-1" />
                      {new Date(connection.updatedAt).toLocaleDateString()}
                    </div>
                  </DataTableCell>
                  <DataTableCell className="whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex items-center justify-end space-x-2">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/mcp-connections/${connection.id}`);
                        }}
                        className="text-indigo-600 dark:text-indigo-400 hover:text-indigo-900 dark:hover:text-indigo-300"
                      >
                        View
                      </button>
                      {/* The one delete affordance (task 864). The inline
                          two-step Confirm/Cancel swap this replaces named
                          nothing that used the resource and offered no way
                          back from a mis-click. */}
                      <DeleteResourceControl
                        flag="innovate.fe.mcp-connections.delete"
                        entity="mcp-connections"
                        entityLabel="MCP connection"
                        name={connection.name}
                        id={connection.id}
                        stopPropagation
                        body={<p>Agents attached to it stop reaching its tools.</p>}
                        onDelete={() => deleteMcpConnection.mutateAsync(connection.id)}
                      />
                    </div>
                  </DataTableCell>
                </DataTableRow>
              ))}
            </DataTableBody>
          </DataTable>
        )}

        {pagination && pagination.totalPages > 1 && (
          <div className="mt-6">
            <Pagination
              pagination={pagination}
              onPageChange={setPage}
              onLimitChange={setLimit}
            />
          </div>
        )}
      </div>
    </div>
  );
}
